import type { Account } from '@repo-level/core-profile/api/profile';
import type { Navigation } from 'navi';
import type { CompPoints, User, Wallet } from '../../api/auth';
import {
  deletePlayerAccount,
  compPoints as fetchCompPoints,
  player as fetchPlayer,
  playerAccounts as fetchPlayerAccounts,
  logout as logoutRequest,
  playerSettings,
  setPlayerAccounts,
} from '../../api/auth';
import type { UserAction, UserState } from './reducer';
import { REASON_FOR_UPDATING_USER, USER_ACTION_TYPE } from './reducer';

type DispatchAction<D, T> = (
  dispatch: React.Dispatch<D>,
  params: T
) => Promise<User | void>;

type DispatchActionUser<T> = DispatchAction<UserAction, T>;

type FetchUserParams = {
  reasonForUpdatingUser: REASON_FOR_UPDATING_USER;
  defaultState: UserState;
  updateDefaultUserStatuses: (user: User) => User;
};

export const fetchUser: DispatchActionUser<FetchUserParams> = async (
  dispatch,
  { reasonForUpdatingUser, defaultState, updateDefaultUserStatuses }
) => {
  try {
    dispatch({ type: USER_ACTION_TYPE.USER_START_PENDING });

    const { data: user } = await fetchPlayer();

    const isAuthenticated = user.id != null;

    if (isAuthenticated) {
      dispatch({ type: USER_ACTION_TYPE.WALLETS_START_PENDING });

      let wallets: Wallet[] = [];
      let extendedWallets: Wallet[] = [];

      const [
        { data: dataExtendedWallets },
        { data: dataWallets },
        { data: compPoints },
      ] = await Promise.all([
        fetchPlayerAccounts(false),
        fetchPlayerAccounts(),
        fetchCompPoints(),
      ]);

      wallets = dataWallets;
      extendedWallets = dataExtendedWallets;

      if (wallets.length === 0 && user?.currency != null) {
        const { data: dataUpdatedWallets } = await setPlayerAccounts(
          user.currency
        );

        wallets = dataUpdatedWallets;
      }

      dispatch({
        type: USER_ACTION_TYPE.USER_FETCH,
        payload: {
          user: updateDefaultUserStatuses(user),
          compPoints,
          reasonForUpdatingUser,
          wallets,
          extendedWallets,
          isAuthenticated,
        },
      });

      return user;
    }

    dispatch({
      type: USER_ACTION_TYPE.USER_FETCH,
      payload: {
        user: defaultState.user,
        compPoints: defaultState.compPoints,
        reasonForUpdatingUser,
        wallets: defaultState.wallets,
        extendedWallets: defaultState.extendedWallets,
        isAuthenticated: defaultState.isAuthenticated,
      },
    });
  } catch {
    dispatch({
      type: USER_ACTION_TYPE.USER_FETCH,
      payload: {
        user: defaultState.user,
        compPoints: defaultState.compPoints,
        reasonForUpdatingUser,
        wallets: defaultState.wallets,
        extendedWallets: defaultState.extendedWallets,
        isAuthenticated: defaultState.isAuthenticated,
      },
    });
  } finally {
    dispatch({ type: USER_ACTION_TYPE.USER_STOP_PENDING });
    dispatch({ type: USER_ACTION_TYPE.WALLETS_STOP_PENDING });
  }
};

type RefreshUserParams = {
  prevUser: User | undefined;
  updateDefaultUserStatuses: (user: User) => User;
};

export const refreshUser: DispatchActionUser<RefreshUserParams> = async (
  dispatch,
  { prevUser, updateDefaultUserStatuses }
) => {
  try {
    const { data: user } = await fetchPlayer();
    if (JSON.stringify(prevUser?.statuses) === JSON.stringify(user.statuses))
      return;

    dispatch({
      type: USER_ACTION_TYPE.USER_REFRESH,
      payload: updateDefaultUserStatuses(user),
    });
  } catch {}
};

type InsertUserParams = {
  user: User;
  reasonForUpdatingUser: REASON_FOR_UPDATING_USER;
};

export const insertUser: DispatchActionUser<InsertUserParams> = async (
  dispatch,
  { user, reasonForUpdatingUser }
) => {
  try {
    dispatch({ type: USER_ACTION_TYPE.WALLETS_START_PENDING });

    let wallets: Wallet[] = [];

    const [{ data: dataWallets }, { data: compPoints }] = await Promise.all([
      fetchPlayerAccounts(),
      fetchCompPoints(),
    ]);

    wallets = dataWallets;

    if (wallets.length === 0 && user?.currency != null) {
      const { data: dataUpdatedWallets } = await setPlayerAccounts(
        user.currency
      );

      wallets = dataUpdatedWallets;
    }

    dispatch({
      type: USER_ACTION_TYPE.USER_INSERT,
      payload: {
        user,
        compPoints,
        wallets,
        reasonForUpdatingUser,
        isAuthenticated: user.id != null,
      },
    });
  } catch {
  } finally {
    dispatch({ type: USER_ACTION_TYPE.WALLETS_STOP_PENDING });
  }
};

type ResetUserParams = {
  navigation: Navigation;
  defaultState: UserState;
};

export const resetUser: DispatchActionUser<ResetUserParams> = async (
  dispatch,
  { navigation, defaultState }
) => {
  try {
    dispatch({ type: USER_ACTION_TYPE.USER_START_PENDING });

    await logoutRequest();

    dispatch({
      type: USER_ACTION_TYPE.USER_RESET,
      payload: { defaultState },
    });

    navigation.navigate('/');
  } catch {
  } finally {
    dispatch({ type: USER_ACTION_TYPE.USER_STOP_PENDING });
    dispatch({ type: USER_ACTION_TYPE.WALLETS_STOP_PENDING });
  }
};

type UpdateUserReason = { reasonForUpdatingUser: REASON_FOR_UPDATING_USER };

export const updateUserReason: DispatchActionUser<UpdateUserReason> = async (
  dispatch,
  { reasonForUpdatingUser }
) => {
  dispatch({
    type: USER_ACTION_TYPE.USER_UPDATE_REASON,
    payload: { reasonForUpdatingUser },
  });
};

type ChangeUserWalletParams = {
  currency: Wallet['currency'];
  defaultState: UserState;
  updateDefaultUserStatuses: (user: User) => User;
};

export const changeUserWallet: DispatchActionUser<
  ChangeUserWalletParams
> = async (dispatch, { currency, defaultState, updateDefaultUserStatuses }) => {
  try {
    dispatch({ type: USER_ACTION_TYPE.WALLETS_START_PENDING });

    await setPlayerAccounts(currency);
    await fetchUser(dispatch, {
      reasonForUpdatingUser: REASON_FOR_UPDATING_USER.DEPOSIT,
      defaultState,
      updateDefaultUserStatuses,
    });
  } catch {
  } finally {
    dispatch({ type: USER_ACTION_TYPE.WALLETS_STOP_PENDING });
  }
};

type DeleteUserWalletParams = {
  currency: Wallet['currency'];
  defaultState: UserState;
  updateDefaultUserStatuses: (user: User) => User;
};

export const deleteUserWallet: DispatchActionUser<
  DeleteUserWalletParams
> = async (dispatch, { currency, defaultState, updateDefaultUserStatuses }) => {
  try {
    dispatch({ type: USER_ACTION_TYPE.WALLETS_START_PENDING });

    await deletePlayerAccount(currency);
    await fetchUser(dispatch, {
      reasonForUpdatingUser: REASON_FOR_UPDATING_USER.DEPOSIT,
      defaultState,
      updateDefaultUserStatuses,
    });
  } catch {
    dispatch({ type: USER_ACTION_TYPE.WALLETS_STOP_PENDING });
  }
};

type SetAccountParams = { account: Account | null };

export const setAccount: DispatchActionUser<SetAccountParams> = async (
  dispatch,
  { account }
) => {
  dispatch({
    type: USER_ACTION_TYPE.SET_ACCOUNT,
    payload: { account },
  });
};

type SetCurrentAmountParams = {
  currentAmount: number | string;
};

export const setCurrentAmount: DispatchActionUser<
  SetCurrentAmountParams
> = async (dispatch, { currentAmount }) =>
  dispatch({
    type: USER_ACTION_TYPE.SET_CURRENT_AMOUNT,
    payload: { currentAmount },
  });

type SetUpdateUserParams = {
  user: Partial<User>;
};

export const updateUser: DispatchActionUser<SetUpdateUserParams> = async (
  dispatch,
  { user }
) => {
  dispatch({
    type: USER_ACTION_TYPE.USER_UPDATE,
    payload: { user },
  });
};

type SetAfterLoginRedirectParams = {
  url: string;
};

export const setAfterLoginRedirect: DispatchActionUser<
  SetAfterLoginRedirectParams
> = async (dispatch, { url }) => {
  dispatch({
    type: USER_ACTION_TYPE.AFTER_LOGIN_REDIRECT_SET,
    payload: { url },
  });
};

export const resetAfterLoginRedirect: DispatchActionUser<{}> = async (
  dispatch
) => {
  dispatch({
    type: USER_ACTION_TYPE.AFTER_LOGIN_REDIRECT_RESET,
  });
};

export const fetchPlayerSettings: DispatchActionUser<{}> = async (dispatch) => {
  try {
    dispatch({
      type: USER_ACTION_TYPE.PLAYER_SETTINGS_START_PENDING,
    });

    const { data } = await playerSettings();

    dispatch({
      type: USER_ACTION_TYPE.PLAYER_SETTINGS_FETCH,
      payload: data,
    });
  } catch {
  } finally {
    dispatch({
      type: USER_ACTION_TYPE.PLAYER_SETTINGS_STOP_PENDING,
    });
  }
};

type UpdateUserWalletsParams = {
  defaultState: UserState;
};

export const updateUserWallet: DispatchActionUser<
  UpdateUserWalletsParams
> = async (dispatch, { defaultState }) => {
  try {
    let wallets: Wallet[] = [];
    let extendedWallets: Wallet[] = [];

    const [{ data: dataExtendedWallets }, { data: dataWallets }] =
      await Promise.all([fetchPlayerAccounts(false), fetchPlayerAccounts()]);

    wallets = dataWallets;
    extendedWallets = dataExtendedWallets;

    if (wallets.length === 0 && defaultState.user?.currency) {
      const { data: dataUpdatedWallets } = await setPlayerAccounts(
        defaultState.user.currency
      );
      wallets = dataUpdatedWallets;
    }

    dispatch({
      type: USER_ACTION_TYPE.WALLETS_UPDATE,
      payload: {
        wallets,
        extendedWallets,
      },
    });
  } catch {}
};

type SetCompPointsParams = {
  compPoints: CompPoints | undefined;
};

export const setCompPoints: DispatchActionUser<SetCompPointsParams> = async (
  dispatch,
  { compPoints }
) => {
  dispatch({
    type: USER_ACTION_TYPE.SET_COMP_POINTS,
    payload: { compPoints },
  });
};

type SetCurrentPaymentMethodIdParams = {
  currentPaymentMethodId: string | undefined;
};

export const setCurrentPaymentMethodId: DispatchActionUser<
  SetCurrentPaymentMethodIdParams
> = async (dispatch, { currentPaymentMethodId }) => {
  dispatch({
    type: USER_ACTION_TYPE.SET_CURRENT_PAYMENT_METHOD_ID,
    payload: { currentPaymentMethodId },
  });
};
